import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { createEffect } from "@ngrx/effects";
import { catchError, map, switchMap } from "rxjs";
import { LocalStorageService } from "../services/local-storage.service";
import { MeetingRoomsKioskService } from "../services/meeting-rooms-kiosk.service";
import { ToastService } from "../services/toast.service";
import { MeetingRoomsKioskModel } from "./model";

@Injectable({
  providedIn: "root",
})
export class MeetingRoomsKioskEffects {
  private readonly model = inject(MeetingRoomsKioskModel);
  private readonly toastService = inject(ToastService);
  private readonly service = inject(MeetingRoomsKioskService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly router = inject(Router);

  loadKioskMode = createEffect(() =>
    this.model.actions.listen.loadKioskMode$.pipe(
      switchMap((payload) => {
        return this.service.loadKioskMode(payload.roomId).pipe(
          map((data) =>
            this.model.actions.create.loadKioskModeSuccess({ data }),
          ),
          catchError((error: HttpErrorResponse) => {
            if (!navigator.onLine) {
              this.toastService.error(
                $localize`:@@meeting-rooms-kiosk|error-internet-connection-message:Please check your network settings. The kiosk will automatically sync the data when connection is restored.`,
                $localize`:@@meeting-rooms-kiosk|error-internet-connection-title:No internet connection.`,
                20_000,
              );
            } else if (
              error.status === 404 &&
              (error?.error as { errorCode?: string })?.errorCode ===
                "room_not_found"
            ) {
              this.localStorageService.clearTokens();
              void this.router.navigate(["/setup"]);
            } else if (error.status !== 401) {
              this.toastService.error(
                $localize`:@@meeting-rooms-kiosk|error-load-kiosk-mode:Error loading meeting room data`,
              );
            }
            return [this.model.actions.create.loadKioskModeFailure({ error })];
          }),
        );
      }),
    ),
  );

  loadEventDetails = createEffect(() =>
    this.model.actions.listen.loadEventDetails$.pipe(
      switchMap((payload) =>
        this.service.loadEventDetails(payload).pipe(
          map((eventDetails) => {
            return this.model.actions.create.loadEventDetailsSuccess({
              eventDetails,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error(
              $localize`:@@meeting-rooms-kiosk|error-loading-event-details:Error loading event details`,
            );
            return [
              this.model.actions.create.loadEventDetailsFailure({ error }),
            ];
          }),
        ),
      ),
    ),
  );

  createBooking = createEffect(() =>
    this.model.actions.listen.createBooking$.pipe(
      switchMap((payload) =>
        this.service.createBooking(payload).pipe(
          map((accessToken) => {
            return this.model.actions.create.createBookingSuccess(accessToken);
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error(
              $localize`:@@meeting-rooms-kiosk|error-create-kiosk-booking:Error creating meeting room booking`,
            );
            return [this.model.actions.create.createBookingFailure({ error })];
          }),
        ),
      ),
    ),
  );

  checkInBooking = createEffect(() =>
    this.model.actions.listen.checkInBooking$.pipe(
      switchMap((payload) =>
        this.service.checkInBooking(payload).pipe(
          map((accessToken) => {
            return this.model.actions.create.checkInBookingSuccess(accessToken);
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error(
              $localize`:@@meeting-rooms-kiosk|error-check-in:Error checking in to meeting`,
            );
            return [this.model.actions.create.checkInBookingFailure({ error })];
          }),
        ),
      ),
    ),
  );

  finishBooking = createEffect(() =>
    this.model.actions.listen.finishBooking$.pipe(
      switchMap((payload) =>
        this.service.finishBooking(payload).pipe(
          map(() => {
            return this.model.actions.create.finishBookingSuccess();
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error(
              $localize`:@@meeting-rooms-kiosk|error-finish-booking:Error ending meeting`,
            );
            return [this.model.actions.create.finishBookingFailure({ error })];
          }),
        ),
      ),
    ),
  );
  exitKioskMode = createEffect(() =>
    this.model.actions.listen.exitKioskMode$.pipe(
      switchMap((payload) =>
        this.service.exitKioskMode(payload.roomId).pipe(
          map(() => {
            this.localStorageService.clearTokens();
            void this.router.navigate(["/setup"]);
            return this.model.actions.create.exitKioskModeSuccess();
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastService.error(
              $localize`:@@meeting-rooms-kiosk|error-exit-kiosk-mode:Error exiting kiosk mode`,
            );
            return [this.model.actions.create.exitKioskModeFailure({ error })];
          }),
        ),
      ),
    ),
  );
}
